import api from "@/lib/axiosConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

const baseRoute = "back/api/sculpture-edition-mouvements";

const _sculptureEditionMouvementSchema = z.object({
  id: z.string(),
  sculptureEditionId: z.string(),
  instanceId: z.string(),
  description: z.string(),
  lieu: z.string(),
  dateDepart: z.string(),
  dateRetour: z.string(),
  constatEtatDepart: z.boolean(),
  constatEtatRetour: z.boolean(),
  notes: z.string(),
  sequence: z.number(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type SculptureEditionMouvement = z.infer<
  typeof _sculptureEditionMouvementSchema
>;

const _createSculptureEditionMouvementSchema =
  _sculptureEditionMouvementSchema.omit({
    id: true,
    instanceId: true,
    sequence: true,
    createdAt: true,
    updatedAt: true,
  });

export type CreateSculptureEditionMouvement = z.infer<
  typeof _createSculptureEditionMouvementSchema
>;

export type EditSculptureEditionMouvement = Omit<
  CreateSculptureEditionMouvement,
  "sculptureEditionId"
>;

const mouvementsQueryKey = (sculptureEditionId: string | undefined) => [
  "sculpture-edition-mouvements",
  sculptureEditionId,
];

// ─── GET LIST ────────────────────────────────────────────────────────────────

async function getSculptureEditionMouvements(sculptureEditionId: string) {
  const { data } = await api.get<SculptureEditionMouvement[]>(baseRoute, {
    params: { sculptureEditionId },
  });
  return data;
}

export function useGetSculptureEditionMouvements(
  sculptureEditionId: string | undefined,
) {
  return useQuery({
    queryKey: mouvementsQueryKey(sculptureEditionId),
    queryFn: () =>
      sculptureEditionId
        ? getSculptureEditionMouvements(sculptureEditionId)
        : Promise.resolve([]),
    enabled: !!sculptureEditionId,
  });
}

// ─── CREATE ──────────────────────────────────────────────────────────────────

async function createSculptureEditionMouvement(
  params: CreateSculptureEditionMouvement,
) {
  const { data } = await api.post<SculptureEditionMouvement>(baseRoute, params);
  return data;
}

export function useCreateSculptureEditionMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSculptureEditionMouvement,
    onSuccess: (_, { sculptureEditionId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(sculptureEditionId),
      });
    },
  });
}

// ─── UPDATE ──────────────────────────────────────────────────────────────────

async function editSculptureEditionMouvement({
  mouvementId,
  mouvement,
}: {
  mouvementId: string;
  sculptureEditionId: string;
  mouvement: EditSculptureEditionMouvement;
}) {
  const { data } = await api.put<SculptureEditionMouvement>(
    `${baseRoute}/${mouvementId}`,
    mouvement,
  );
  return data;
}

export function useEditSculptureEditionMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editSculptureEditionMouvement,
    onSuccess: (_, { sculptureEditionId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(sculptureEditionId),
      });
    },
  });
}

// ─── DELETE ──────────────────────────────────────────────────────────────────

async function deleteSculptureEditionMouvement({
  mouvementId,
}: {
  mouvementId: string;
  sculptureEditionId: string;
}) {
  await api.delete(`${baseRoute}/${mouvementId}`);
}

export function useDeleteSculptureEditionMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSculptureEditionMouvement,
    onSuccess: (_, { sculptureEditionId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(sculptureEditionId),
      });
    },
  });
}

// ─── DUPLICATE ───────────────────────────────────────────────────────────────

async function duplicateSculptureEditionMouvement({
  mouvementId,
}: {
  mouvementId: string;
  sculptureEditionId: string;
}) {
  const { data } = await api.post<SculptureEditionMouvement>(
    `${baseRoute}/${mouvementId}/duplicate`,
  );
  return data;
}

export function useDuplicateSculptureEditionMouvement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: duplicateSculptureEditionMouvement,
    onSuccess: (_, { sculptureEditionId }) => {
      queryClient.invalidateQueries({
        queryKey: mouvementsQueryKey(sculptureEditionId),
      });
    },
  });
}

// ─── REORDER ─────────────────────────────────────────────────────────────────

async function updateSculptureEditionMouvementSequence({
  newSequence,
  sculptureEditionId,
}: {
  newSequence: string[];
  sculptureEditionId: string;
}) {
  await api.put(`${baseRoute}/sequence`, {
    sculptureEditionId,
    sequence: newSequence,
  });
}

export function useUpdateSculptureEditionMouvementSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSculptureEditionMouvementSequence,
    onMutate: async ({ newSequence, sculptureEditionId }) => {
      const key = mouvementsQueryKey(sculptureEditionId);
      await queryClient.cancelQueries({ queryKey: key });

      const previous =
        queryClient.getQueryData<SculptureEditionMouvement[]>(key);

      if (previous) {
        const byId = new Map(previous.map((m) => [m.id, m]));
        const reordered = newSequence
          .map((id, index) => {
            const m = byId.get(id);
            return m ? { ...m, sequence: index } : undefined;
          })
          .filter((m): m is SculptureEditionMouvement => m !== undefined);
        queryClient.setQueryData(key, reordered);
      }

      return { previous };
    },
    onError: (_err, { sculptureEditionId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          mouvementsQueryKey(sculptureEditionId),
          context.previous,
        );
      }
    },
  });
}
